import {
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type ISelectBoxProps,
    type SelectBoxValue,
    DirectionProvider,
    NCoreUIKitTheme,
    createFlagIcon,
    SelectBox,
    Button,
    Switch,
    Flag,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

type Country = {
    name: string;
    code: string;
};

type CountrySelectProps = ISelectBoxProps<Country>;

const NAME = "Flag";

const USAGE_CODE = `import {
    createFlagIcon,
    Button,
    Flag
} from "ncore-ui-kit";

<Flag
    code="tr"
    size="large"
/>

<Button
    icon={createFlagIcon("tr")}
    onPress={handleCountry}
    title="Türkiye"
/>`;

const PICKER_CODE = `<SelectBox
    renderOptionIcon={({
        item
    }) => <Flag
        isDecorative={true}
        code={item.__key}
    />}
    icon={country ? createFlagIcon(country) : undefined}
    onChange={(items) => setCountry(items[0]?.__key)}
    titleExtractor={(item) => item.name}
    keyExtractor={(item) => item.code}
    spreadBehaviour="stretch"
    data={countries}
    title="Country"
/>`;

const ADDRESS_CODE = `<Flag
    baseUrl="https://example.com/flags"
    code="tr"
/>

const MyFlag = (props: IFlagProps) => <Flag
    baseUrl="https://example.com/flags"
    {...props}
/>;

createFlagIcon("tr", {
    baseUrl: "https://example.com/flags"
});`;

const OVERVIEW_CODES = [
    "tr",
    "us",
    "gb",
    "de",
    "jp",
    "eu",
    "un",
    "gb-sct"
];

const PICKER_CODES = [
    "tr",
    "de",
    "fr",
    "gb",
    "us",
    "br",
    "jp",
    "in"
];

const BUTTON_ITEMS = [
    {
        variant: "filled",
        code: "tr"
    },
    {
        variant: "tonal",
        code: "de"
    },
    {
        variant: "outline",
        code: "jp"
    }
] as const;

const SIZES = [
    "small",
    "large",
    24,
    48
] as const;

const SLOT_SIZE = 48;

const getCountryName = (code: string, language: string) => {
    try {
        return new Intl.DisplayNames([
            language
        ], {
            type: "region"
        }).of(code.toUpperCase()) ?? code.toUpperCase();
    } catch {
        return code.toUpperCase();
    }
};

const getCountryTitle = (country: Country) => country.name;

const getCountryKey = (country: Country) => country.code;

const renderCountryFlag: NonNullable<CountrySelectProps["renderOptionIcon"]> = ({
    item
}) => <Flag
    isDecorative={true}
    code={item.__key}
/>;

const SampleCell = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            flexDirection: "column",
            gap: spaces.spacingSm,
            alignItems: "center",
            display: "flex"
        }}
    >
        <div
            style={{
                justifyContent: "center",
                alignItems: "center",
                height: SLOT_SIZE,
                display: "flex"
            }}
        >
            {children}
        </div>
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("flagPage.fallbackTitle"),
        id: "fallback"
    },
    {
        title: t("flagPage.pickerTitle"),
        id: "picker"
    },
    {
        title: t("flagPage.componentsTitle"),
        id: "components"
    },
    {
        title: t("flagPage.addressTitle"),
        id: "address"
    },
    {
        title: t("flagPage.languageTitle"),
        id: "language"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const FlagPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        iconSizes,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isRtl,
        setIsRtl
    ] = useState(false);

    const [
        country,
        setCountry
    ] = useState<string | undefined>("tr");

    const countries: Array<Country> = PICKER_CODES.map((code) => ({
        name: getCountryName(code, language),
        code
    }));

    const handleCountryChange = (nextValue: SelectBoxValue) => {
        setCountry(nextValue === "" ? undefined : String(nextValue));
    };

    return <Article>
        <ComponentHeader
            lead={t("flagPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("flagPage.overviewText")}
            </Paragraph>
            <Preview>
                {
                    OVERVIEW_CODES.map((code) => <SampleCell
                        label={code}
                        key={code}
                    >
                        <Flag
                            code={code}
                            size={32}
                        />
                    </SampleCell>)
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("flagPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <SampleCell
                        label={typeof size === "number" ? `${size}` : `${size} · ${iconSizes[size]}`}
                        key={size}
                    >
                        <Flag
                            size={size}
                            code="tr"
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("flagPage.fallbackTitle")}
            id="fallback"
        >
            <Paragraph>
                {t("flagPage.fallbackText")}
            </Paragraph>
            <Preview
                caption={t("flagPage.fallbackCaption")}
            >
                <SampleCell
                    label="tr"
                >
                    <Flag
                        size={SLOT_SIZE}
                        code="tr"
                    />
                </SampleCell>
                <SampleCell
                    label="x"
                >
                    <Flag
                        size={SLOT_SIZE}
                        code="x"
                    />
                </SampleCell>
            </Preview>
        </Section>
        <Section
            title={t("flagPage.pickerTitle")}
            id="picker"
        >
            <Paragraph>
                {t("flagPage.pickerText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <SelectBox
                    icon={country ? createFlagIcon(country) : undefined}
                    placeholder={t("flagPage.pickerPlaceholder")}
                    renderOptionIcon={renderCountryFlag}
                    onValueChange={handleCountryChange}
                    title={t("flagPage.pickerLabel")}
                    titleExtractor={getCountryTitle}
                    keyExtractor={getCountryKey}
                    spreadBehaviour="stretch"
                    value={country ?? ""}
                    data={countries}
                />
            </Preview>
            <CodeBlock
                code={PICKER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("flagPage.componentsTitle")}
            id="components"
        >
            <Paragraph>
                {t("flagPage.componentsText")}
            </Paragraph>
            <Preview
                caption={t("flagPage.componentsCaption")}
                isStacked={true}
            >
                <Switch
                    title={t("flagPage.directionSwitch")}
                    onCheckedChange={setIsRtl}
                    isChecked={isRtl}
                />
                <DirectionProvider
                    direction={isRtl ? "rtl" : "ltr"}
                    style={{
                        justifyContent: "center",
                        gap: spaces.spacingMd,
                        flexDirection: "row",
                        alignItems: "center",
                        flexWrap: "wrap"
                    }}
                >
                    {
                        BUTTON_ITEMS.map((item) => <Button
                            title={getCountryName(item.code, language)}
                            icon={createFlagIcon(item.code)}
                            onPress={() => undefined}
                            variant={item.variant}
                            key={item.code}
                        />)
                    }
                    <Icon
                        icon={createFlagIcon("br")}
                        size="large"
                    />
                </DirectionProvider>
            </Preview>
        </Section>
        <Section
            title={t("flagPage.addressTitle")}
            id="address"
        >
            <Paragraph>
                {t("flagPage.addressText")}
            </Paragraph>
            <BulletList
                items={[
                    t("flagPage.addressOffline"),
                    t("flagPage.addressPrivacy"),
                    t("flagPage.addressPolicy")
                ]}
            />
            <CodeBlock
                code={ADDRESS_CODE}
                language="tsx"
            />
            <Paragraph>
                {t("flagPage.addressWrap")}
            </Paragraph>
        </Section>
        <Section
            title={t("flagPage.languageTitle")}
            id="language"
        >
            <Callout>
                {t("flagPage.languageText")}
            </Callout>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("flagPage.a11yName"),
                    t("flagPage.a11yDecorative"),
                    t("flagPage.a11yDirection"),
                    t("flagPage.a11yFocus")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("flagPage.propCode"),
                        type: "string",
                        name: "code"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\" | number",
                        description: t("flagPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("flagPage.propBaseUrl"),
                        name: "baseUrl",
                        type: "string"
                    },
                    {
                        description: t("flagPage.propIsDecorative"),
                        defaultValue: "false",
                        name: "isDecorative",
                        type: "boolean"
                    },
                    {
                        description: t("flagPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("flagPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("flagPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("flagPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("flagPage.helperText")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default FlagPage;
